import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import hash from 'object-hash';
import classNames from 'classnames';

import style from './NavigationGroup.module.scss';
import itemStyle from '@core/Page/LeftMenu/NavigationItem/NavigationItem.module.scss';

import type { Optional } from 'chaika-common-types';

import { NavigationItemParams } from '@store/App/LeftMenu';
import { IconTypeToComponentMap } from '@components/Icon/IconType';
import { translate } from '@chaika/i18n/helper';

import { NavigationItem } from '../../NavigationItem';
import { findActiveSubItem } from '../../utils/findActiveSubItem';

interface Props {
    item: NavigationItemParams;
    subItems: NavigationItemParams[];
    isMenuOpen: boolean;
    onLinkClick(): void;
    onSubItemTitleClick(): void;
}

export function NavigationGroup({ item, subItems, isMenuOpen, onLinkClick, onSubItemTitleClick }: Props) {
    const { pathname } = useLocation();
    const [isExpandedByUser, setIsExpandedByUser] = useState<Optional<boolean>>(undefined);
    // DOM-узел активного подпункта нужен только для прокрутки списка к нему
    const activeSubItemRef = useRef<HTMLAnchorElement>(null);

    const activeSubItem = findActiveSubItem(subItems, pathname);
    const isExpanded = isExpandedByUser ?? activeSubItem !== undefined;
    const TitleIcon = IconTypeToComponentMap[item.iconType];

    useEffect(() => {
        if (isMenuOpen && isExpanded) {
            activeSubItemRef.current?.scrollIntoView({ block: 'nearest' });
        }
    }, [isMenuOpen, isExpanded, activeSubItem]);

    const handleTitleClick = (): void => {
        if (isMenuOpen) {
            setIsExpandedByUser(!isExpanded);

            return;
        }

        onSubItemTitleClick();
        setIsExpandedByUser(true);
    };

    if (!isMenuOpen && activeSubItem) {
        const ActiveSubItemIcon = IconTypeToComponentMap[activeSubItem.iconType];

        return (
            <div className={classNames([style.collapsedGroup, itemStyle.active])}>
                <div
                    className={style.collapsedGroupTitle}
                    onClick={handleTitleClick}
                    data-test={item.dataTest}
                    data-testid={item.dataTest}
                >
                    <div className={itemStyle.icon}>
                        <TitleIcon />
                    </div>
                </div>
                <Link
                    to={activeSubItem.link}
                    className={style.collapsedGroupActiveSubItem}
                    onClick={onLinkClick}
                    draggable={false}
                    data-test={activeSubItem.dataTest}
                    data-testid={activeSubItem.dataTest}
                >
                    <div className={itemStyle.icon}>
                        <ActiveSubItemIcon />
                    </div>
                </Link>
            </div>
        );
    }

    return (
        <div className={style.root}>
            <div
                className={classNames([itemStyle.root, style.title])}
                onClick={handleTitleClick}
                data-test={item.dataTest}
                data-testid={item.dataTest}
            >
                <div className={itemStyle.icon}>
                    <TitleIcon />
                </div>
                <div className={itemStyle.title}>{translate(item.title)}</div>
            </div>
            {isMenuOpen &&
                isExpanded &&
                subItems.map(subItem => (
                    <NavigationItem
                        isSubItem
                        isActive={subItem === activeSubItem}
                        ref={subItem === activeSubItem ? activeSubItemRef : undefined}
                        dataTest={subItem.dataTest}
                        key={hash(subItem)}
                        title={translate(subItem.title)}
                        link={subItem.link}
                        iconType={subItem.iconType}
                        onLinkClick={onLinkClick}
                    />
                ))}
        </div>
    );
}
